import type { AccountSummary, Rewards, Statement, Holiday } from "mkm-types";
import { toRefs } from "@vueuse/core";
import { useConfig } from "~/constants";

const { baseUrl, headers } = useConfig();

const INVALID_REWARDS_STATUSES: Set<string> = new Set(["D", "N", "E"]);

const displayRewardsClub = (rewards: Rewards): boolean => {
  return !INVALID_REWARDS_STATUSES.has(rewards.status);
};

const INVALID_HOLIDAY_STATUSES: Set<string> = new Set(["NONE"]);

const displayHolidayScheme = (holiday: Holiday): boolean => {
  return !INVALID_HOLIDAY_STATUSES.has(holiday.destination);
};

const getAccountBalance = (statement: Array<Statement>): number => {
  return statement.find((record: Statement) => record.label === "Total account balance")?.value || 0;
};

export interface UseStatementState {
  accountSummary: AccountSummary | null;
  accountBalance: number;
  displayRewardsClub: boolean;
  displayHolidayScheme: boolean;
  errorFetchingStatement: boolean;
  loadingStatement: boolean;
}

const useStatement = () => {
  const { baseUrl, headers } = useConfig();
  const state = useState<UseStatementState>(`useStatement`, () => ({
    accountSummary: null,
    accountBalance: 0,
    displayRewardsClub: false,
    displayHolidayScheme: false,
    errorFetchingStatement: false,
    loadingStatement: false,
  }));

  const fetchStatement = async () => {
    state.value.loadingStatement = true;

    const { data: accountSummary, error } = await useAsyncData("getAccountDetails", () =>
      $fetch<AccountSummary>(`${baseUrl}/getAccountDetails`, {
        headers,
        method: "POST",
        credentials: "include",
      }),
    );

    if (error.value) {
      state.value.accountSummary = null;
      state.value.errorFetchingStatement = true;
      state.value.loadingStatement = false;

      return;
    }

    if (!accountSummary.value) {
      return;
    }

    state.value.accountSummary = accountSummary.value;

    if (accountSummary.value.statement) state.value.accountBalance = getAccountBalance(accountSummary.value.statement);

    if (accountSummary.value.rewards) state.value.displayRewardsClub = displayRewardsClub(accountSummary.value.rewards);

    if (accountSummary.value.holiday)
      state.value.displayHolidayScheme = displayHolidayScheme(accountSummary.value.holiday);

    state.value.errorFetchingStatement = false;
    state.value.loadingStatement = false;
  };

  return {
    fetchStatement,
    ...toRefs(state.value),
  };
};

export default useStatement;
